import { observable, action, runInAction, makeObservable } from "mobx";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { API } from "General/API";
import { fileUploadStore, UploadedTmpFile } from "../FileUpload/FileUploadStore";

export class FilenameAssistantStore extends CoreStore {
  isVisible = false;
  rules: API_FilenameAssistantResponse["rules"] = null;
  typesMap: {
    [key: string]: { [key: string]: API_FilenameAssistantRuleResponse };
  } = null;
  data: API_UploadFile[] = [];
  fileNames: API_FilenameAssistantFilenameResponse[] = [];
  dividers: { [ruleName: string]: string } = null;
  noRulesError: boolean = false;
  unknowPDLRules: string[] = [];

  constructor() {
    super();

    makeObservable(this, {
      isVisible: observable,
      rules: observable,
      typesMap: observable,
      data: observable,
      fileNames: observable,
      dividers: observable,
      noRulesError: observable,
      unknowPDLRules: observable,
      clear: action.bound,
      setDividerForRule: action.bound,
      setRuleForType: action.bound,
      toggleModalVisible: action.bound,
      setFiles: action.bound,
      fetch: action.bound,
      fetchPdlAssistant: action.bound,
    });
  }

  url() {
    return API_URL.DOCUMENTS.getDocumentsByFolderId(projectsDataStore.projectId, documentsTableStore.currentFolderId);
  }

  clear() {
    this.isVisible = false;
    this.data = [];
    this.dividers = null;
    this.rules = null;
    this.typesMap = null;
    this.noRulesError = false;
    this.unknowPDLRules = [];
  }

  @handleStoreState()
  async fetch(file?: UploadedTmpFile | API_UploadFile) {
    const body = file ? { filenames: [file.name] } : { filenames: this.data.map((item) => item.name) };

    try {
      const params = fileUploadStore.module === "WORKFLOW" ? { module: "WORKFLOW" } : null;
      const response: API_FilenameAssistantResponse = await API.newData(
        API_URL.FILES.getFilenameAssistentRequest(projectsDataStore.projectId),
        { ...body, directoryId: fileUploadStore.folderId },
        params
      );
      runInAction(() => {
        this.rules = response.rules;
        this.typesMap = this.setRuleForType(response.filetypeMapping);
        this.fileNames = response.filenames;
        this.dividers = this.setDividerForRule(response.rules);
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetchPdlAssistant() {
    try {
      this.noRulesError = false;
      this.unknowPDLRules = [];
      const response: API_FilenameAssistantResponse = await API.getData(
        API_URL.PDL.getPDLAssistant(projectsDataStore.projectId)
      );
      runInAction(() => {
        this.rules = response.rules;
        const typesMap = {};
        const allTypes = {};
        for (const ruleName in response.rules) {
          const rule = response.rules[ruleName];
          allTypes[ruleName] = true;
          rule.validFiletypes.forEach((type) => {
            if (!typesMap[type]) {
              typesMap[type] = [];
            }
            typesMap[type].push(ruleName);
          });
        }
        this.typesMap = this.setRuleForType(typesMap);
        //   this.fileNames = response.filenames;
        this.dividers = this.setDividerForRule(response.rules);
      });
    } catch (error) {
      runInAction(() => {
        this.noRulesError = true;
      });
      throw error;
    }
  }

  setUnknownPDLRules(unknowPDLRules: string[]) {
    this.unknowPDLRules = unknowPDLRules;
  }

  getFileRuleno(initialName: string) {
    if (!initialName) {
      return null;
    }
    let ruleName = "";
    const blocks = this.fileNames.find(({ name }) => name === initialName);
    if (blocks) {
      if (blocks.proposedRule) {
        return blocks.proposedRule.rulename;
      }
      const ruleObject = Object.keys(this.rules).find(
        (ruleKey) => (this.rules[ruleKey] as API_FilenameAssistantRuleResponse).ruleno === blocks.ruleno
      );
      ruleObject && (ruleName = ruleObject);
    }
    return ruleName;
  }

  setDividerForRule(rules: { [key: string]: API_FilenameAssistantRuleResponse }) {
    const data = {};
    for (const ruleKey in rules) {
      data[ruleKey] = rules[ruleKey].divider;
    }
    return data;
  }

  setRuleForType(filetypeMapping: { [key: string]: string[] }) {
    const data = {};
    const sharedRules = {};

    if (Object.keys(filetypeMapping).includes("*")) {
      filetypeMapping["*"].forEach((ruleKey) => (sharedRules[ruleKey] = this.rules[ruleKey]));
    }

    for (const typeKey in filetypeMapping) {
      filetypeMapping[typeKey].forEach((ruleKey) => {
        data[typeKey] = {
          ...data[typeKey],
          [ruleKey]: this.rules[ruleKey],
          ...sharedRules,
        };
      });
    }
    return data;
  }

  toggleModalVisible() {
    this.isVisible = !this.isVisible;
  }

  setFiles(data: API_UploadFile[]) {
    this.data = data;
  }

  getFirstRule(): API_FilenameAssistantRuleResponse {
    const ruleKeys = Object.keys(this.rules);
    return this.rules[ruleKeys[0]];
  }
}

export const filenameAssistantStore = new FilenameAssistantStore();
